import { useT } from "../../i18n";
import { cx } from "../../lib/cx";
import { useFormat } from "../../lib/format";
import { useCountUp } from "../../hooks/useCountUp";
import { useStats } from "../../hooks/useStats";
import { useFiltersStore, type CountMode } from "../../store/filtersStore";

function Kpi({ value, label }: { value: number; label: string }) {
  const animated = useCountUp(value);
  const fmt = useFormat();
  return (
    <div className="text-right">
      <div className="data-value text-2xl font-medium leading-none text-text-primary">
        {fmt.int(animated)}
      </div>
      <div className="text-xs text-text-tertiary">{label}</div>
    </div>
  );
}

function ModeButton({ mode, current, onSelect, label, hint }: {
  mode: CountMode;
  current: CountMode;
  onSelect: (m: CountMode) => void;
  label: string;
  hint: string;
}) {
  const active = current === mode;
  return (
    <button
      type="button"
      title={hint}
      aria-pressed={active}
      onClick={() => onSelect(mode)}
      className={cx(
        "rounded px-2 py-0.5 text-xs transition-colors",
        active
          ? "bg-accent-subtle font-medium text-accent"
          : "text-text-tertiary hover:text-text-secondary",
      )}
    >
      {label}
    </button>
  );
}

export function HeaderStats() {
  const t = useT();
  const { count, totalRate } = useStats();
  const countMode = useFiltersStore((s) => s.countMode);
  const setCountMode = useFiltersStore((s) => s.setCountMode);

  return (
    <div className="flex items-center gap-4">
      <div className="flex items-end gap-4">
        <Kpi value={count} label={t.stats.detections} />
        <div className="h-8 w-px bg-border-subtle" />
        <Kpi value={totalRate} label={t.stats.emissions} />
      </div>

      {/* The count mode is spelled out, not an icon: otherwise the numbers look
          like they jump for no reason (brief §8). */}
      <div className="flex flex-col items-start">
        <span className="text-[0.625rem] uppercase tracking-wide text-text-tertiary">
          {t.stats.countLabel}
        </span>
        <div className="flex items-center gap-0.5">
          <ModeButton
            mode="extent"
            current={countMode}
            onSelect={setCountMode}
            label={t.stats.modeExtent}
            hint={t.stats.modeExtentHint}
          />
          <ModeButton
            mode="country"
            current={countMode}
            onSelect={setCountMode}
            label={t.stats.modeCountry}
            hint={t.stats.modeCountryHint}
          />
        </div>
      </div>
    </div>
  );
}
